import {
  Grid,
  IconButton,
  IconColor,
  IconName,
  IconSize,
  InfoIcon,
  LinkButton,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import { ScreeningRuleDraft } from '../../../data/model/screeningRules'
import { useSetScreeningRulesV2 } from '../../../hooks/audience/setScreeningRulesV2'
import useGetDraftAudience from '../../../hooks/audience/useGetDraftAudience'
import { useGetScreeningRules } from '../../../hooks/audience/useGetScreeningRules'
import DialogScreeningRule from './DialogScreeningRule'
import {
  describeRule,
  getSourceQuestions,
  rulesInOrder,
  splitLegacyOrRules,
  toDraft
} from './screeningRules.utils'

/**
 * Screening rules belong to the screener rather than to any one audience question, so they are authored here
 * beside the question list rather than on a question card.
 */
const ScreeningRulesList: React.FC = () => {
  const { draftAudience } = useGetDraftAudience()
  const { screeningRules } = useGetScreeningRules()
  const setScreeningRules = useSetScreeningRulesV2()
  // A rule's identity is its index in the list. That is safe here because nothing refetches the rules query
  // except the rules mutation itself, this dialog is closed before that runs, and it is modal — so the list
  // cannot reorder underneath it. `index === drafts.length` means a rule being added.
  const [editing, setEditing] = useState<
    { draft: ScreeningRuleDraft; index: number } | undefined
  >()

  const questions = getSourceQuestions(draftAudience?.members)
  const drafts = splitLegacyOrRules(rulesInOrder(screeningRules)).map(toDraft)

  const save = (next: ScreeningRuleDraft[]) => {
    setEditing(undefined)
    void setScreeningRules(next)
  }

  const onSaveEdited = (draft: ScreeningRuleDraft) => {
    if (!editing) return
    const edited = { ...draft, edited: true }
    save(
      editing.index === drafts.length
        ? [...drafts, edited]
        : drafts.map((existing, index) =>
            index === editing.index ? edited : existing
          )
    )
  }

  return (
    <Grid container direction="column" gap="8px">
      <Grid container alignItems="center" gap="4px">
        <Text
          weight={TextWeight.Bold}
          highlight={TextHighlight.Standard}
          size={TextSize.m}
        >
          Screening rules
        </Text>
        <InfoIcon text="A screening rule can span several questions — use it to screen out on a combination of answers, which the per-question qualifying toggle can't express. Any rule that matches screens the respondent out, so their order does not matter." />
      </Grid>

      {drafts.length === 0 ? (
        <Text size={TextSize.s} highlight={TextHighlight.Background}>
          No screening rules. Respondents are screened out by the qualifying
          options on each question.
        </Text>
      ) : (
        drafts.map((draft, index) => (
          <Grid
            // biome-ignore lint/suspicious/noArrayIndexKey: the index is the rule's identity
            key={index}
            container
            alignItems="center"
            gap="4px"
            wrap="nowrap"
          >
            <Text size={TextSize.s} highlight={TextHighlight.Background}>
              {`R${index + 1} Screen out if ${describeRule(draft, questions)}`}
            </Text>
            <LinkButton
              highlight={TextHighlight.Emphasis}
              size={TextSize.s}
              weight={TextWeight.SemiBold}
              noPadding
              onClick={() => setEditing({ draft, index })}
            >
              Edit
            </LinkButton>
            <IconButton
              iconName={IconName.DeleteOutline}
              iconSize={IconSize.Small}
              iconColor={IconColor.Emphasis}
              ariaLabel={`Delete screening rule ${index + 1}`}
              onClick={() => save(drafts.filter((_, i) => i !== index))}
            />
          </Grid>
        ))
      )}

      {questions.length > 0 && (
        <LinkButton
          highlight={TextHighlight.Emphasis}
          size={TextSize.s}
          weight={TextWeight.SemiBold}
          noPadding
          onClick={() =>
            setEditing({
              draft: { rows: [] },
              index: drafts.length
            })
          }
        >
          Add screening rule
        </LinkButton>
      )}

      {editing && (
        <DialogScreeningRule
          open
          draft={editing.draft}
          questions={questions}
          onSave={onSaveEdited}
          onClose={() => setEditing(undefined)}
        />
      )}
    </Grid>
  )
}

export default ScreeningRulesList
